<script lang="ts">
import { cva, type VariantProps } from 'class-variance-authority'

export const AButtonVariantOptions = {
  default: 'atabix-button--default',
  destructive: 'atabix-button--destructive',
  outline: 'atabix-button--outline',
  secondary: 'atabix-button--secondary',
  ghost: 'atabix-button--ghost',
  link: 'atabix-button--link',
  closer: 'atabix-button--closer',
  neutral: 'atabix-button--neutral',
} as const

export const AButtonSizeOptions = {
  default: 'atabix-button--size-default',
  xs: 'atabix-button--size-xs',
  sm: 'atabix-button--size-sm',
  lg: 'atabix-button--size-lg',
  icon: 'atabix-button--size-icon',
} as const

export const AButtonWidthOptions = {
  default: '',
  full: 'w-full',
} as const

export const AButtonVariants = cva('atabix-button', {
  variants: {
    variant: AButtonVariantOptions,
    size: AButtonSizeOptions,
    width: AButtonWidthOptions,
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export type AButtonVariantsType = VariantProps<typeof AButtonVariants>
</script>

<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts" setup>
import { createReusableTemplate } from '@vueuse/core'
import { LoadingIcon } from 'mdi-vue3'
import { Primitive, type PrimitiveProps } from 'reka-ui'
import { computed, type DefineComponent, getCurrentInstance, type HTMLAttributes, toRefs } from 'vue'
import { markRaw } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { cn } from '../../../lib/utils'
import type { Testable } from '../../../main'
import { ATooltip, ATooltipContent, ATooltipProvider, ATooltipTrigger } from '../tooltip'

export type AButtonClickEventDetails = AButtonProperties & { event: MouseEvent }

export interface AButtonProperties extends Testable, PrimitiveProps {
  variant?: AButtonVariantsType['variant']
  size?: AButtonVariantsType['size']
  width?: AButtonVariantsType['width']
  class?: HTMLAttributes['class']

  /**
   * The label of the button
   */
  // eslint-disable-next-line vue/require-default-prop
  id?: string
  title?: string
  disabled?: boolean
  loading?: boolean
  type?: 'button' | 'submit' | 'reset'

  icon?: DefineComponent | string
  tooltip?: string
  iconPosition?: 'left' | 'right'
  to?: RouteLocationRaw
  target?: '_blank' | '_self' | '_parent' | '_top' | string
  click?: (event: MouseEvent) => Promise<void | boolean> | void | boolean
}

interface AButtonEvents {
  /**
   * Emitted when the button is clicked
   */
  (event: 'click', button: AButtonClickEventDetails): void
}

defineOptions({ inheritAttrs: false })

const properties = withDefaults(defineProps<AButtonProperties>(), {
  variant: 'default',
  size: 'default',
  width: 'default',
  icon: undefined,
  tooltip: undefined,
  loading: false,
  disabled: false,
  class: undefined,
  to: undefined,
  target: '_self',
  type: 'button',
  iconPosition: 'left',
  title: undefined,
  click: undefined,
  as: 'button',
})

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const { icon } = toRefs(properties)

const internalIcon = computed(() => {
  if (properties.loading) {
    return markRaw(LoadingIcon)
  }

  return typeof icon.value === 'string' ? 'img' : icon.value
})

const internalSource = computed(() => {
  return internalIcon.value === 'img' ? icon.value : undefined
})

const emit = defineEmits<AButtonEvents>()

const onClick = (event: MouseEvent) => {
  if (properties.disabled || properties.loading) {
    return
  }
  if (properties.click) {
    properties.click(event)
  }

  emit('click', { ...properties, event })
}

const classes = computed(() =>
  cn(
    AButtonVariants({ variant: properties.variant, size: properties.size, width: properties.width }),
    {
      'atabix-button--action': (properties.click || properties.to || hasClickEventListener) && !properties.disabled && !properties.loading,
      'atabix-button--disabled': properties.disabled || properties.loading,
      'atabix-button--loading': properties.loading,
      'cursor-pointer': !properties.click,
    },
    properties.class,
  ),
)

const hasClickEventListener = computed(() => !!getCurrentInstance()?.vnode.props?.onClick)

const iconClasses = computed(() => {
  if (properties.loading) {
    return 'animate-spin size-5'
  }

  return internalIcon.value === 'img' ? 'h-5' : 'size-5'
})

const isToExternal = computed(() => {
  return url.value.startsWith('http')
})

const url = computed<string>(() => {
  if (typeof properties.to !== 'string') {
    return ''
  }

  return properties.to
})
</script>

<template>
  <DefineTemplate>
    <Primitive
      v-if="!to"
      v-bind="$attrs"
      :id="id"
      :as="type === 'submit' ? 'button' : as"
      :as-child="asChild"
      :class="classes"
      :data-test
      :disabled="disabled || loading"
      :type="type"
      @click="onClick"
    >
      <component :is="internalIcon" v-if="internalIcon && iconPosition === 'left'" :class="iconClasses" :src="internalSource" />
      <slot>{{ title }}</slot>
      <component :is="internalIcon" v-if="internalIcon && iconPosition === 'right'" :class="iconClasses" :src="internalSource" />
    </Primitive>
    <a v-else-if="typeof to === 'string' && isToExternal" :class="classes" v-bind="$attrs" :data-test :href="to" :target="target">
      <component :is="internalIcon" v-if="internalIcon && iconPosition === 'left'" :class="iconClasses" :src="internalSource" />
      <slot>{{ title }}</slot>
      <component :is="internalIcon" v-if="internalIcon && iconPosition === 'right'" :class="iconClasses" :src="internalSource" />
    </a>
    <router-link v-else-if="to" :class="classes" v-bind="$attrs" :data-test :to="to">
      <component :is="internalIcon" v-if="internalIcon && iconPosition === 'left'" :class="iconClasses" :src="internalSource" />
      <slot>{{ title }}</slot>
      <component :is="internalIcon" v-if="internalIcon && iconPosition === 'right'" :class="iconClasses" :src="internalSource" />
    </router-link>
  </DefineTemplate>

  <template v-if="tooltip">
    <ATooltipProvider>
      <ATooltip>
        <ATooltipTrigger>
          <ReuseTemplate />
        </ATooltipTrigger>
        <!--        @TODO -->
        <ATooltipContent>
          <p>{{ tooltip }}</p>
        </ATooltipContent>
      </ATooltip>
    </ATooltipProvider>
  </template>
  <template v-else>
    <ReuseTemplate />
  </template>
</template>

<style lang="scss">
.atabix-button {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  @apply whitespace-nowrap;
  @apply rounded-md;
  @apply text-base;
  @apply font-semibold;
  @apply ring-offset-background;
  @apply transition-colors;
  @apply focus-visible:outline-none;
  @apply focus-visible:ring-2;
  @apply focus-visible:ring-foreground;
  @apply focus-visible:ring-offset-2;
  @apply select-none;
}

.atabix-button--disabled {
  @apply cursor-not-allowed;
  @apply opacity-50;
}

.atabix-button--loading {
  @apply cursor-not-allowed;
}

.atabix-button--action {
  @apply cursor-pointer;
}

.atabix-button--default {
  @apply bg-button-primary-bg;
  @apply text-button-primary-text;
  @apply hover:bg-button-primary-bg-pressed;
}

.atabix-button--destructive {
  @apply bg-button-destructive-bg;
  @apply text-button-destructive-text;
  @apply hover:bg-background-alert-90;
}

.atabix-button--outline {
  @apply border;
  @apply text-foreground;
  @apply border-button-outline-border;
  @apply bg-button-outline-bg;
  @apply hover:bg-background-hover;
  @apply hover:text-button-outline-text;
}

.atabix-button--neutral {
  //
}

.atabix-button--secondary {
  @apply bg-tag-background;
  @apply text-foreground;
  @apply hover:bg-tag-background-80;
}

.atabix-button--ghost {
  @apply hover:bg-button-ghostneutral-bg-pressed;
  @apply hover:text-button-ghostneutral-text;
}

.atabix-button--link {
  @apply text-button-ghostprimary-text;
  @apply underline-offset-4;
  @apply hover:underline;
}

.atabix-button--closer {
  @apply w-5;
  @apply h-5;
  @apply stroke-1;
  @apply bg-none;
  @apply border-none;
  @apply text-red-500;
}

.atabix-button--size-default {
  @apply box-border;
  @apply min-w-10 h-10;
  @apply p-2.5;
  @apply gap-2;
}

.atabix-button--size-xs {
  @apply min-w-7 h-7;
  @apply rounded;
  @apply px-1;
}

.atabix-button--size-sm {
  @apply min-w-9 h-9;
  @apply rounded-md;
}

.atabix-button--size-lg {
  @apply min-w-11 h-11;
  @apply rounded-md;
  @apply px-8;
}

.atabix-button--size-icon {
  @apply h-10;
  @apply w-10;
}
</style>
